import {
  DeleteForever,
  FileUpload,
  Gavel,
  GroupsRounded,
  Google,
  Home,
  ManageAccounts,
  MonetizationOn,
  RecentActors,
  RoomPreferences,
  Lock,
  LockOpenOutlined,
  SubdirectoryArrowRight,
} from "@mui/icons-material";
import Globe from "../../../static/assets/images/globe.svg";
import { Button } from "@mui/material";
import React, { useMemo } from "react";
import { ApiRouteHelpers } from "../root/apiRoutes";
import {
  OrganizationDataRecipientDto,
  OrganizationDataRecipientPlatformDto,
} from "../service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../service/server/dto/OrganizationDto";
import { RequestHandlingInstructionDto } from "../service/server/dto/RequestHandlingInstructionDto";
import { RetentionReasonDto } from "../service/server/dto/RetentionReasonDto";
import { Abbr } from "../../polaris/components/Abbr";
import { DataRecipientLogo } from "../../polaris/components/DataRecipientLogo";
import {
  DisclosureMeta,
  DisclosureState,
  GoogleAnalyticsHelp,
} from "../../polaris/components/organisms/data-recipients/DataRecipientDisclosureSettings";
import { SettingsItem } from "../../polaris/components/organisms/data-recipients/SettingsIndicator";
import { installedAppsForVendor } from "../../polaris/util/resources/vendorUtils";
import { isStorageLocation, isThirdPartyRecipient } from "../../polaris/util/vendors";
import { lexicalJoin } from "../../polaris/util/text";
import { useDataRequest } from "./api";
import { Api } from "../service/Api";
import { OrganizationManagedAdNetworkDto } from "../service/server/dto/OrganizationManagedAdNetworkDto";

export const useDataRetentionItems = (
  vendor: OrganizationDataRecipientDto,
  appPlatforms: OrganizationDataRecipientPlatformDto,
  organizationVendors: OrganizationDataRecipientDto[],
  fetchedInstructions: RequestHandlingInstructionDto[],
  retentionReasons: RetentionReasonDto[],
): SettingsItem[] => {
  const platforms = useMemo(
    () => organizationVendors?.filter((ov) => ov.isPlatform),
    [organizationVendors],
  );
  const installedIn = useMemo(() => new Set(appPlatforms?.installed ?? []), [appPlatforms]);

  const visiblePlatforms = useMemo(
    () => vendor && !vendor.isPlatform && !vendor.isStandalone && !isThirdPartyRecipient(vendor),
    [vendor],
  );

  const foundInstruction = useMemo(
    () => fetchedInstructions?.find((i) => i.vendorId === vendor?.vendorId),
    [fetchedInstructions, vendor?.vendorId],
  );

  const vendorRetentionReasons = useMemo(
    () =>
      foundInstruction?.retentionReasons?.map((r) => retentionReasons.find((x) => x.slug === r)),
    [foundInstruction, retentionReasons],
  );

  return useMemo(() => {
    const platformSettings = platforms
      .filter(() => visiblePlatforms)
      .map((platform) => {
        return {
          id: `platform-${platform.id}`,
          icon: <DataRecipientLogo size={24} dataRecipient={platform} />,
          label: installedIn.has(platform.vendorId) ? (
            <>
              {vendor?.name} is an installed app in <strong>{platform.name}</strong>
            </>
          ) : (
            <>
              {vendor?.name} is not an installed app in <strong>{platform.name}</strong>
            </>
          ),
          response: installedIn.has(platform.vendorId),
        };
      });

    const hasExceptionsToDeletion =
      !!foundInstruction && foundInstruction.processingMethod != "DELETE";
    const dataNotAccessible = vendor.dataAccessibility == "NOT_ACCESSIBLE";
    return [
      ...platformSettings,
      dataNotAccessible
        ? null
        : {
            id: "deletion-exception",
            label: hasExceptionsToDeletion ? (
              <>
                <strong>Exception to deletion</strong> apply to {vendor?.name}
              </>
            ) : (
              <>
                <strong>No Exceptions to Deletion</strong>
              </>
            ),
            icon: DeleteForever,
            subtitle: vendorRetentionReasons?.map((r) => r.reason).join(", "),
            response: hasExceptionsToDeletion,
          },
      {
        id: "accessibility",
        label: dataNotAccessible ? (
          <>
            Data in {vendor?.name} is <strong>not accessible</strong>
          </>
        ) : (
          <>
            Data in {vendor?.name} is <strong>accessible</strong>
          </>
        ),
        icon: dataNotAccessible ? Lock : LockOpenOutlined,
      },
    ].filter((x) => x);
  }, [platforms, installedIn, visiblePlatforms, foundInstruction, vendorRetentionReasons, vendor]);
};

export const useInstalledApps = (
  vendor: OrganizationDataRecipientDto,
  platformApps: OrganizationDataRecipientPlatformDto,
): SettingsItem[] => {
  return useMemo(
    () =>
      installedAppsForVendor(vendor, platformApps).map((app) => ({
        id: app?.id,
        label: app?.name,
        icon: <DataRecipientLogo size={24} dataRecipient={app} />,
        response: null,
        isNested: false,
      })),
    [vendor, platformApps],
  );
};

export const useDataDisclosureItems = (
  org: OrganizationDto,
  dataRecipient: OrganizationDataRecipientDto,
  state: DisclosureState,
  meta: DisclosureMeta,
  answers: Record<string, string>,
  managedAdNetworks?: OrganizationManagedAdNetworkDto[],
) =>
  useMemo(
    () => disclosureItems(org, dataRecipient, state, meta, answers, managedAdNetworks),
    [org, dataRecipient, answers, state, meta, managedAdNetworks],
  );

export const disclosureItems = (
  org: OrganizationDto,
  vendor: OrganizationDataRecipientDto,
  state: DisclosureState,
  meta: DisclosureMeta,
  answers: Record<string, string>,
  managedAdNetworks?: OrganizationManagedAdNetworkDto[],
): SettingsItem[] => {
  if (!vendor) {
    return [];
  }

  const items: SettingsItem[] = [];

  const isUsRegion = vendor.processingRegions.includes("UNITED_STATES");
  const isEeaUkRegion = vendor.processingRegions.includes("EEA_UK");
  const isCanadaRegion = vendor.processingRegions.includes("CANADA");

  if (meta.isCurrentVendorManagingCustomAudiences) {
    items.push({
      id: "current-vendor-manages-custom-audiences",
      icon: GroupsRounded,
      label: (
        <>
          {vendor.name} <b>manages Custom Audience Lists</b> for {org.name}
        </>
      ),
    });
  }

  if (org.featureGdpr && !isStorageLocation(vendor) && isEeaUkRegion) {
    items.push({
      id: "processor-classification",
      icon: state.processorClassification === "Processor" ? RoomPreferences : ManageAccounts,
      label: (
        <>
          {vendor.name} is a <strong>{state.processorClassification}</strong> of personal data
          (GDPR)
        </>
      ),
      response: state.processorClassification === "Processor",
      action: actionsForProcessorDocumentation(org, vendor).map((a) => (
        <Button
          key={a.href}
          className="ml-md"
          target="_blank"
          rel="noopener noreferrer"
          color="inherit"
          href={a.href}
        >
          {a.label}
        </Button>
      )),
    });

    items.push({
      id: "international-data-transfers",
      icon: Gavel,
      label: (
        <>
          <strong>International transfer rules</strong>{" "}
          {vendor.gdprInternationalDataTransferRulesApply !== false ? "apply" : "do not apply"} to{" "}
          {vendor.name}'s data processing
        </>
      ),
      response: vendor.gdprInternationalDataTransferRulesApply,
      action: actionsForSccDocumentation(org, vendor).map((a) => (
        <Button
          key={a.href}
          className="ml-md"
          target="_blank"
          rel="noopener noreferrer"
          color="inherit"
          href={a.href}
        >
          {a.label}
        </Button>
      )),
    });
  }

  // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
  // if (meta.showFacebookLdu) {
  //   items.push({
  //     id: "facebook-ldu",
  //     icon: FacebookOutlined,
  //     label: `${org.name} ${state.facebookLdu ? "will" : "will not"} enable LDU for ${vendor.name}`,
  //     response: state.facebookLdu,
  //     helpText: <FacebookLduHelp />,
  //   });
  // }

  // if (meta.showGoogleRdp) {
  //   items.push({
  //     id: "google-rdp",
  //     icon: Google,
  //     label: `${org.name} ${state.googleRdp ? "will" : "will not"} enable RDP for ${vendor.name}`,
  //     response: state.googleRdp,
  //     helpText: <GoogleRdpHelp />,
  //   });
  // }

  if (meta.showSharing && !isStorageLocation(vendor)) {
    const sharingDefinition =
      "“Sharing” means providing personal information to ad networks for purposes of behavioral, or interest-based advertising.";
    items.push({
      id: "sharing",
      icon: Home,
      label: state.ccpaIsSharing ? (
        <>
          {org.name}{" "}
          <Abbr title={sharingDefinition}>
            <strong>"shares" personal information</strong>
          </Abbr>{" "}
          with {vendor.name}
        </>
      ) : (
        <>
          {org.name}{" "}
          <Abbr title={sharingDefinition}>
            <strong>does not "share" personal information</strong>
          </Abbr>{" "}
          with {vendor.name}
        </>
      ),
      response: state.ccpaIsSharing,
      helpText: sharingDefinition,
    });
  }

  if (meta.showGoogleAnalytics && !isStorageLocation(vendor)) {
    const isGaDataSharingOff = answers["google-analytics-data-sharing"] == "true";
    items.push({
      id: "google-analytics",
      icon: Google,
      label: isGaDataSharingOff
        ? `${org.name} has turned off (or will turn off) data sharing in Google Analytics`
        : `${org.name} has not turned off (nor will it turn off) data sharing in Google Analytics`,
      response: isGaDataSharingOff,
      helpText: <GoogleAnalyticsHelp />,
    });
  }

  if (meta.showSelling && (isUsRegion || isCanadaRegion) && !isStorageLocation(vendor)) {
    const sellingDefinition =
      '"Selling" means providing personal data in exchange for money or anything else of value.';
    items.push({
      id: "sells-info",
      icon: MonetizationOn,
      label:
        state.ccpaIsSelling || state.isAlwaysDataSelling ? (
          <>
            {org.name}{" "}
            <Abbr title={sellingDefinition}>
              <strong>"sells" personal information</strong>
            </Abbr>{" "}
            to {vendor.name}
          </>
        ) : (
          <>
            {org.name}{" "}
            <Abbr title={sellingDefinition}>
              <strong>does not "sell"</strong>
            </Abbr>{" "}
            personal data to {vendor.name}
          </>
        ),
      response: state.ccpaIsSelling,
      helpText: sellingDefinition,
    });
  }

  if (
    meta.showSelling &&
    (state.isAlwaysDataSelling ||
      (state.ccpaIsSelling && (state.isUploadVendor || state.isAlwaysUploadVendor))) &&
    !isStorageLocation(vendor)
  ) {
    items.push({
      id: "uploads-info",
      icon: FileUpload,
      label: (
        <>
          {org.name} <strong>transmits or uploads</strong> consumer data to {vendor.name}
        </>
      ),
      response: state.isUploadVendor || state.isAlwaysUploadVendor || state.isAlwaysDataSelling,
      isNested: true,
    });
  }

  if (meta.showCustomAudience && !isStorageLocation(vendor)) {
    const customAudienceDefinition =
      'Custom Audience is a service offered by most ad networks in which a business can upload a list of their customers or contacts, and the ad network will either deliver ads directly to those people and/or use it to create a "look-alike" audience.';
    items.push({
      id: "custom-audience",
      icon: RecentActors,
      label: state.usesCustomAudience ? (
        <>
          {org.name} uses the{" "}
          <Abbr title={customAudienceDefinition}>
            <strong>Audience feature</strong>
          </Abbr>{" "}
          with {vendor.name}
        </>
      ) : (
        <>
          {org.name} does not use the{" "}
          <Abbr title={customAudienceDefinition}>
            <strong>Audience feature</strong>
          </Abbr>{" "}
          with {vendor.name}
        </>
      ),
      response: state.usesCustomAudience,
      helpText: customAudienceDefinition,
    });

    // Show items for actual custom audience managers from the database
    if (state.usesCustomAudience) {
      // Get ad network manager relationships for this network from the database
      const adNetworkManagerRelationships =
        managedAdNetworks?.filter((item) => item.networkId === vendor.id) || [];

      // Find the actual manager vendor objects from the relationships list
      const managersAsVendors = adNetworkManagerRelationships
        .map((relationship) =>
          meta.potentialCustomAudienceManagers?.find(
            (manager) => manager.id === relationship.managerId,
          ),
        )
        .filter(Boolean); // Remove any undefined entries
      managersAsVendors.forEach((manager) => {
        if (manager) {
          items.push({
            id: `custom-audience-managed-by-${manager.id}`,
            icon: SubdirectoryArrowRight,
            isNested: true,
            label: (
              <>
                Audience lists in {vendor.name} are <b>managed via {manager.name}</b>
              </>
            ),
            response: true, // These are the actual active relationships
          });
        }
      });
    }
  }

  if (state.processingRegions) {
    let regionText = lexicalJoin(state.processingRegions);
    regionText = regionText.replace("UNITED_STATES", "the US");
    regionText = regionText.replace("EEA_UK", "EEA/UK/CH");
    regionText = regionText.replace("CANADA", "Canada");

    if (state.processingRegions.length == 1) {
      regionText += " only";
    }

    if (org.featureGdpr || org.featurePipeda) {
      items.push({
        id: "processing-regions",
        icon: <Globe height="24px" />,
        label: (
          <>
            {vendor.name} processes data in <strong>{regionText}</strong>
          </>
        ),
        response: org.featureGdpr || org.featurePipeda,
      });
    }
  }

  return items;
};

type ActionProps = {
  href: string;
  label: React.ReactNode;
};

const actionsForProcessorDocumentation = (
  org: OrganizationDto,
  dataRecipient: OrganizationDataRecipientDto,
): ActionProps[] => {
  let actions = [];

  if (!org.featureGdpr) {
    return actions;
  }

  if (dataRecipient.gdprProcessorGuaranteeUrl) {
    actions.push({
      href: dataRecipient.gdprProcessorGuaranteeUrl,
      label: "Public TOS",
    });
  } else if (dataRecipient.gdprControllerGuaranteeUrl) {
    actions.push({
      href: dataRecipient.gdprControllerGuaranteeUrl,
      label: "Public TOS",
    });
  }

  if (dataRecipient.gdprProcessorGuaranteeFileKey) {
    actions.push({
      href: ApiRouteHelpers.storage.vendorAgreements.byType(
        org.id,
        dataRecipient.vendorId,
        "processorGuarantee",
      ),
      label: "View Agreement",
    });
  } else if (dataRecipient.gdprControllerGuaranteeFileKey) {
    actions.push({
      href: ApiRouteHelpers.storage.vendorAgreements.byType(
        org.id,
        dataRecipient.vendorId,
        "controllerGuarantee",
      ),
      label: "View Agreement",
    });
  } else if (dataRecipient.gdprProcessorLanguageUrl) {
    actions.push({
      href: dataRecipient.gdprProcessorLanguageUrl,
      label: "View Agreement",
    });
  }

  return actions;
};

const actionsForSccDocumentation = (
  org: OrganizationDto,
  dataRecipient: OrganizationDataRecipientDto,
): ActionProps[] => {
  let actions = [];

  if (!org.featureGdpr) {
    return actions;
  }

  if (dataRecipient.gdprSccUrl) {
    actions.push({
      href: dataRecipient.gdprSccUrl,
      label: "Public TOS",
    });
  }

  if (dataRecipient.gdprSccFileKey) {
    actions.push({
      href: ApiRouteHelpers.storage.vendorAgreements.byType(org.id, dataRecipient.vendorId, "scc"),
      label: "View Agreement",
    });
  } else if (dataRecipient.gdprSccDocumentationUrl) {
    actions.push({
      href: dataRecipient.gdprSccDocumentationUrl,
      label: "View Agreement",
    });
  }

  return actions;
};

// Hook to manage organization managed ad networks
export const useManagedAdNetworks = (organizationId: string) => {
  const { result, request, refresh } = useDataRequest({
    queryKey: ["managedAdNetworks", organizationId],
    api: () => Api.managedAdNetworks.getAllManagedAdNetworks(organizationId),
  });

  // The query result can be a truthy non-array (e.g. a malformed entry rehydrated
  // from the persisted "polarisData" localStorage cache, or a non-JSON 2xx response
  // body), which crashes consumers that call .filter/.map on it (POL-7654).
  const managedAdNetworks = Array.isArray(result) ? result : undefined;

  const getManagersForNetwork = (networkId: string): string[] => {
    if (!managedAdNetworks) return [];
    return managedAdNetworks
      .filter((item) => item.networkId.toString() === networkId)
      .map((item) => item.managerId.toString());
  };

  const addManagedRelationship = async (managerId: string, networkId: string) => {
    await Api.managedAdNetworks.createManagedAdNetwork(organizationId, {
      managerId,
      networkId,
    });
    refresh();
  };

  const removeManagedRelationship = async (managerId: string, networkId: string) => {
    await Api.managedAdNetworks.deleteManagedAdNetworkByIds(
      organizationId,
      parseInt(managerId),
      parseInt(networkId),
    );
    refresh();
  };

  return {
    managedAdNetworks: managedAdNetworks || [],
    getManagersForNetwork,
    addManagedRelationship,
    removeManagedRelationship,
    isLoading: request.running,
    error: request.errorMessage,
    refresh,
  };
};
